import type { SubscriptionDisplayInfo } from '@saaga/shared/interfaces/billing.interface'
import type { ReactNode } from 'react'

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarRail,
} from '@/components/ui/sidebar'

import type { SidebarNavGroup } from './sidebar-nav-item.interface'
import { SidebarNavMenu } from './sidebar-nav-menu.molecule'
import { SidebarNewChatButton } from './sidebar-new-chat-button.molecule'
import { SidebarOrgSwitcher } from './sidebar-org-switcher.molecule'
import { SidebarUserMenu } from './sidebar-user-menu.molecule'

interface OrganizationData {
  id: string
  name: string
  slug: string | null
  imageUrl: string | null
}

interface UserData {
  name: string
  email: string
  imageUrl: string | null
}

interface AppSidebarProps {
  user: UserData
  activeOrganization: OrganizationData | null
  organizations: OrganizationData[]
  navGroups: SidebarNavGroup[]
  subscriptionInfo: SubscriptionDisplayInfo | null
  /**
   * Clerk-sourced Early Access flag. When true, the sidebar renders an
   * "Early Access" tag alongside the plan name. Resolved by the parent
   * container via `useOrganizationEarlyAccess()` so the organism stays
   * dumb/presentational.
   */
  isEarlyAccess?: boolean
  onSignOut: () => void
  onOrganizationSelect: (orgId: string) => void
  onCreateOrganization: () => void
  onNewChat: () => void
}

export const AppSidebar = ({
  user,
  activeOrganization,
  organizations,
  navGroups,
  subscriptionInfo,
  isEarlyAccess = false,
  onSignOut,
  onOrganizationSelect,
  onCreateOrganization,
  onNewChat,
}: AppSidebarProps): ReactNode => (
  <Sidebar collapsible="icon">
    <SidebarHeader>
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarOrgSwitcher
            activeOrganization={activeOrganization}
            organizations={organizations}
            onOrganizationSelect={onOrganizationSelect}
            onCreateOrganization={onCreateOrganization}
            planInfo={
              subscriptionInfo
                ? {
                    planName: subscriptionInfo.planName,
                    status: subscriptionInfo.status,
                    earlyAccess: isEarlyAccess,
                  }
                : null
            }
          />
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarHeader>

    <SidebarContent>
      <SidebarNewChatButton onClick={onNewChat} />
      <SidebarNavMenu groups={navGroups} />
    </SidebarContent>

    <SidebarFooter>
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarUserMenu user={user} onSignOut={onSignOut} />
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarFooter>

    <SidebarRail />
  </Sidebar>
)
